type LazyLoader<ValueType> = (yield_: (value: ValueType) => void, delegate_: (other: AsyncIterable<ValueType>, include: (value:ValueType) => boolean) => void) => Promise<boolean>;

export class LazyLoadable<ValueType> {
    loaders: Array<LazyLoader<ValueType>> = [];
    fill(loader: LazyLoader<ValueType>) {
        this.loaders.push(loader);
    }
    // @ts-ignore: TS2564
    [Symbol.asyncIterator]: () => AsyncIterator<ValueType>;
    constructor() {
        this[Symbol.asyncIterator] = () => { return new LazyIterator(this); };
    }
    async toArray(): Promise<ValueType[]> {
        const allItems = [];
        for await (const item of this) {
            allItems.push(item);
        }
        return allItems;
    }
    async toSet(): Promise<Set<ValueType>> {
        const allItems = [];
        for await (const item of this) {
            allItems.push(item);
        }
        return new Set(allItems);
    }
}

export class LazyIterator<ValueType> implements AsyncIterator<ValueType> {
    loaders: Array<LazyLoader<ValueType>> = [];
    yielded:Array<ValueType> = [];
    constructor(loadable: LazyLoadable<ValueType>) {
        this.loaders = [ ... loadable.loaders ];
    }    
    next(value_?: ValueType): Promise<IteratorResult<ValueType>> {
        if(this.yielded.length === 0) {
            if(this.loaders.length === 0) {
                return Promise.resolve({ value: undefined, done: true });
            } else {
                return this._refill().then(() => {
                    if(this.yielded.length === 0) {
                        return { done: true, value: undefined }
                    }
                    return this._pop();
                })
            }
        }
        return Promise.resolve(this._pop());
    }

    _pop() {
        const value = this.yielded[0];
        this.yielded = this.yielded.slice(1);
        if(value === undefined) {
            throw new Error('Undefined value')
        }
        return { value, done: false };
    }

    _yield(value: ValueType) {
        if(value === undefined) {
            throw new Error('Yielding undefined value')
        }
        this.yielded.push(value);
    }
    _delegate(other: AsyncIterable<ValueType>, include: (value:ValueType) => boolean = (value) => true) {
        const iterator = other[Symbol.asyncIterator]();
        this.loaders.push((yield_, delegate_): Promise<boolean> => {
            return iterator.next().then((result) => {
                if(result.value !== undefined && include(result.value)) {
                    yield_(result.value);
                }
                return !result.done;
            })
        });
    }

    _refill(): Promise<ValueType|undefined> {
        const gen = this.loaders[0];
        return gen(this._yield.bind(this), this._delegate.bind(this)).then((callAgain) => {
            if(!callAgain) {
                this.loaders = this.loaders.slice(1);
            }
            if(this.yielded.length === 0 && this.loaders.length > 0) {
                return this._refill();
            } else {
                return Promise.resolve(this._next())
            }
        });
    }

    _next() {
        if(this.yielded.length === 0) {
            return undefined;
        } else {
            const item = this.yielded[0];
            return item;
        }
    }
}
